import React, { useEffect, useState, useMemo } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import {
  Avatar,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  // InputAdornment,
  // Link,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

// import { ReactComponent as UnverifiedMailIcon } from "assets/images/svg/unverified-mail.svg";
// import { ReactComponent as VerifiedMailIcon } from "assets/images/svg/verified-mail.svg";

import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { get, isBoolean } from "lodash";

// import { useRequestOTPMutation } from "apis/OTP";
import getFileExtension from "utils/getFileExtension";

import InputFieldText from "Components/Common/InputFieldText";
import {
  allAdminRoles,
  POWER_CONSUMER_APPLICATION_INCHARGE,
  SUPER_ADMIN,
} from "common/constant";
import SelectField from "Components/Common/SelectField";
import { useLazyGetAuthorizedPersonQuery } from "apis/User";
import { getImageUrl, isAuthorizedUser, onlyNumberKeys } from "utils";
import { getOptionValue, salutationOptions } from "common/enums";
import {
  PlusIcon,
  AuthorizedInfoWrapper,
  ProfileImageWrapper,
  UploadImageButton,
} from "./style";
// import VerifyEmailModal from "Components/Common/VerifyEmailModal";
// import { otpVerificationType } from "common/constant";

const imageExt = ["jpg", "png", "jpeg"];

// const requestOTPType = otpVerificationType["Verification"];

function AuthorizedPersonDetails({ formik, t }) {
  const {
    setFieldValue,
    values: { authorizedPerson },
    errors,
    handleBlur,
    setTouched,
    touched,
    values: {
      extraValues: { userEmail, userMobileNumber },
      id,
      utilityId,
      authorizedPerson: {
        isAuthorizedPerson,
        email,
        mobileNumber,
        isUnRegisteredPerson,
        isEditablePerson,
      },
    },
  } = formik;

  const user = useSelector((state) => state.Login.user);
  const { roles } = useSelector((state) => state.Login);

  // const [isOTPSent, setIsOTPSent] = useState(false);

  const [imgValidationError, setImgValidationError] = useState("");
  const [isError, setIsError] = useState(false);
  // const [isDifferentUser, setIsDifferentUser] = useState(false);

  const [getAuthorizedPersonData, { data, isLoading }] =
    useLazyGetAuthorizedPersonQuery();

  // const [requestOTP, { isLoading: isRequestOTPLoading }] =
  //   useRequestOTPMutation();

  //add isDifferentUser here to disabled all the fields when user is different

  const isAdmin = useMemo(
    () =>
      isAuthorizedUser(
        [SUPER_ADMIN, POWER_CONSUMER_APPLICATION_INCHARGE],
        roles
      ),
    [roles]
  );

  const isDisabled = useMemo(
    () =>
      utilityId
        ? !isAdmin && !isEditablePerson
        : isAuthorizedPerson || !isUnRegisteredPerson,
    [
      isAuthorizedPerson,
      isUnRegisteredPerson,
      isEditablePerson,
      utilityId,
      isAdmin,
    ]
  );

  const differentUserFormik = useFormik({
    initialValues: {
      email: email ?? "",
      mobileNumber: mobileNumber ?? "",
      // isEmailVerified: false,
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter valid email")
        .required("Email is required"),
      mobileNumber: Yup.string()
        .required("Mobile number is required")
        .matches(/^\d{10}$/, "Please enter valid mobile number"),
    }),

    onSubmit: async (values) => {
      try {
        await getUserData(values);
      } catch (error) {
        setFieldValue("authorizedPerson.mobileNumber", values.mobileNumber);
        setFieldValue("authorizedPerson.email", values.email);
        setFieldValue("authorizedPerson.isUnRegisteredPerson", true);
        setIsError(true);
        setFieldValue("authorizedPerson.isEditablePerson", true);
        // setIsDifferentUser(true);
        resetAuthorizedPersonDetailsValue(authorizedPerson, [
          "isEditablePerson",
          "isAuthorizedPerson",
          "isUnRegisteredPerson",
          "mobileNumber",
          "email",
        ]);
        setFieldValue("authorizedPerson.id", 0);
      }
    },
  });

  const profileURL = useMemo(
    () =>
      authorizedPerson?.imageUrl || authorizedPerson.image
        ? authorizedPerson.image
          ? URL.createObjectURL(authorizedPerson.image)
          : getImageUrl(authorizedPerson.imageUrl)
        : "/broken-image.jpg",
    [authorizedPerson?.imageUrl, authorizedPerson.image]
  );

  const imageRequiredMsg = useMemo(
    () =>
      get(touched, "authorizedPerson.imageUrl", "") &&
      errors.authorizedPerson?.imageUrl,
    [
      get(touched, "authorizedPerson.imageUrl", ""),
      errors.authorizedPerson?.imageUrl,
    ]
  );

  const getUserData = async (userData) => {
    const { data: authorizedUserData } = await getAuthorizedPersonData({
      email: encodeURIComponent(userData.email),
      phoneNumber: userData.mobileNumber,
      ...(!!id && { applicationId: id }),
    });
    Object.entries(authorizedUserData).forEach(([key, value]) => {
      if (value || isBoolean(value)) {
        setFieldValue(`authorizedPerson.${key}`, value);
      } else if (!isAuthorizedPerson) {
        setFieldValue(`authorizedPerson.${key}`, "");
      }
    });
    setFieldValue("authorizedPerson.isUnRegisteredPerson", false);
    setIsError(false);
  };

  const resetAuthorizedPersonDetailsValue = (data, keys) => {
    Object.keys(data).forEach((value) => {
      if (!keys.includes(value)) setFieldValue(`authorizedPerson.${value}`, "");
    });
  };

  const clearBtnHandler = () => {
    // differentUserFormik.setFieldValue("isEmailVerified", false);
    differentUserFormik.setFieldValue("mobileNumber", "");
    differentUserFormik.setFieldValue("email", "");
    resetAuthorizedPersonDetailsValue(authorizedPerson, [
      "id",
      "isAuthorizedPerson",
      "isUnRegisteredPerson",
      "isEditablePerson",
    ]);
    setFieldValue("authorizedPerson.isUnRegisteredPerson", false);
    setIsError(false);
    differentUserFormik.setTouched({});
  };

  const unAuthorizedClickHandler = () => {
    setFieldValue("authorizedPerson.isAuthorizedPerson", false);
    differentUserFormik.setFieldValue("email", "");
    differentUserFormik.setFieldValue("mobileNumber", "");
    resetAuthorizedPersonDetailsValue(authorizedPerson, [
      "id",
      "isAuthorizedPerson",
      "isEditablePerson",
      "isUnRegisteredPerson",
    ]);
    setTouched({});
  };

  const isValidFile = (file) => {
    const fileExtension = getFileExtension(file.name)?.toLowerCase();
    let errorMessage = "";

    if (imageExt.includes(fileExtension) && file.size > 600000) {
      errorMessage = "File size should be less than 600kB";
    } else if (!imageExt.includes(fileExtension)) {
      errorMessage =
        "Invalid file format, Please upload images in jpg, png, or jpeg format.";
    }

    setImgValidationError(errorMessage);
    if (errorMessage) {
      setFieldValue("authorizedPerson.image", "");
    }

    return !errorMessage;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && isValidFile(file)) {
      setFieldValue("authorizedPerson.image", file);
    }
    e.target.value = "";
  };

  const authorizedPersonClickHandler = () => {
    setFieldValue("authorizedPerson.isAuthorizedPerson", true);
    setFieldValue("authorizedPerson.image", "");
    setFieldValue("authorizedPerson.imageUrl", "");
    setFieldValue("authorizedPerson.landLine", "");
    setFieldValue("authorizedPerson.designation", "");
    setTouched({});
  };

  const isPowerUtility = useMemo(
    () => getOptionValue("utilityOptions", "Power") === utilityId,
    [utilityId]
  );

  // const generateOTP = async (email) => {
  //   try {
  //     await requestOTP({
  //       username: email,
  //       type: requestOTPType,
  //     });
  //     setIsOTPSent(true);
  //   } catch (error) {}
  // };

  useEffect(() => {
    (async () => {
      if (isAuthorizedPerson && user) {
        try {
          await getUserData(
            isAuthorizedUser(roles, allAdminRoles)
              ? { email: userEmail, mobileNumber: userMobileNumber }
              : user
          );
        } catch (error) {}
      }
    })();
  }, [isAuthorizedPerson, user]);

  return (
    <>
      {/* {(isRequestOTPLoading || isOTPSent) && (
        <VerifyEmailModal
          isOpen={isOTPSent}
          handleClose={() => setIsOTPSent(false)}
          handleOTPverificationSuccess={() => {
            setIsDifferentUser(false);
            differentUserFormik.setFieldValue("isEmailVerified", true);
          }}
          handleOTPVerificationFailed={() => {
            differentUserFormik.setFieldValue("isEmailVerified", false);
          }}
          email={differentUserFormik.values.email}
          isLoading={isRequestOTPLoading}
        />
      )} */}
      <RadioGroup name="authorizedPerson">
        <Grid container>
          <Grid item md={6}>
            <FormControlLabel
              control={<Radio />}
              label={t("Same as registered person")}
              checked={isAuthorizedPerson}
              onChange={authorizedPersonClickHandler}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={<Radio />}
              checked={!isAuthorizedPerson}
              label={t("Different from registered person")}
              onChange={unAuthorizedClickHandler}
            />
          </Grid>
        </Grid>
      </RadioGroup>

      {!isAuthorizedPerson && (
        <>
          <Grid container columnSpacing={2} className="mt-2">
            <Grid item xs={12} sm={6} lg={4}>
              <InputFieldText
                label={t("Email") + "*"}
                name="email"
                formik={differentUserFormik}
                type="email"
                placeholder="Enter Email"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <InputFieldText
                label={t("Mobile Number") + "*"}
                name="mobileNumber"
                formik={differentUserFormik}
                type="number"
                InputProps={{
                  inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                }}
                placeholder={t("Enter Mobile Number")}
              />
            </Grid>

            <Grid item lg={4}>
              <Button
                variant="outlined"
                onClick={() => {
                  // differentUserFormik.setFieldValue("isEmailVerified", false);
                  differentUserFormik.handleSubmit();
                }}
                disabled={isLoading}
                className="me-3 mb-sm-1"
              >
                {t("Search")}
                {isLoading && <CircularProgress size={20} />}
              </Button>
              <Button
                variant="outlined"
                onClick={clearBtnHandler}
                disabled={isLoading}
                className="me-3 mb-sm-1"
              >
                {t("Clear")}
                {isLoading && <CircularProgress size={20} />}
              </Button>
            </Grid>
          </Grid>
          <p className="border-bottom" />
        </>
      )}
      {isError && (
        <Typography color="error" variant="body2">
          {t(
            "Email Id and mobile number do not exists in the system, please specify authorized person details manually"
          )}
        </Typography>
      )}
      {/* {isError && (
        <div className="d-flex ">
          <Typography color="error" variant="body2">
            {differentUserFormik.values.isEmailVerified ? (
              t(
                "Your email is verified, Please enter the authorized person details."
              )
            ) : (
              <>
                {t(
                  "The email address and phone number do not exist in the system. After verifying the email, you can manually enter authorized person information. Click"
                )}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    generateOTP(email);
                  }}
                  sx={{
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  &nbsp;
                  {t("here")}
                </Link>
                &nbsp;{t("to verify email")}
              </>
            )}
          </Typography>
        </div>
      )} */}

      {!isAuthorizedPerson &&
        !isUnRegisteredPerson &&
        !!errors.authorizedPerson &&
        !authorizedPerson.firstName && (
          <Typography color="error" variant="body2">
            {t("Please specify authorized person details")}
          </Typography>
        )}
      <ProfileImageWrapper>
        <UploadImageButton
          component="label"
          disabled={
            isPowerUtility
              ? isDisabled
              : !!authorizedPerson.imageUrl && !isUnRegisteredPerson
          }
        >
          <Avatar src={profileURL} className="avatar" />
          {(isPowerUtility
            ? !isDisabled
            : !authorizedPerson?.imageUrl || isUnRegisteredPerson) && (
            <PlusIcon />
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            onBlur={handleBlur}
            hidden
          />
        </UploadImageButton>
        <Typography>
          {authorizedPerson?.imageUrl || authorizedPerson?.image
            ? t("Profile Picture")
            : t("Upload Picture")}
        </Typography>
        {(imgValidationError || imageRequiredMsg) && (
          // !isDifferentUser &&
          <Typography
            variant="subtitle2"
            className={"input-field-error-message"}
            color="error"
          >
            {imgValidationError || imageRequiredMsg}
          </Typography>
        )}
      </ProfileImageWrapper>
      <Grid container>
        <AuthorizedInfoWrapper>
          <Grid container spacing={2}>
            <Grid item sm={12}>
              <Grid container item columnSpacing={2}>
                <Grid item xs={12} sm={6} lg={4}>
                  <SelectField
                    label={
                      t("Salutation") + `${isUnRegisteredPerson ? "*" : ""}`
                    }
                    name="authorizedPerson.salutation"
                    formik={formik}
                    options={salutationOptions}
                    disabled={isDisabled}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={
                      t("First Name") + `${isUnRegisteredPerson ? "*" : ""}`
                    }
                    name="authorizedPerson.firstName"
                    formik={formik}
                    disabled={isDisabled}
                    placeholder="Enter First Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Middle Name")}
                    name="authorizedPerson.middleName"
                    disabled={isDisabled}
                    formik={formik}
                    placeholder="Enter Middle Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Last Name")}
                    name="authorizedPerson.lastName"
                    disabled={isDisabled}
                    formik={formik}
                    placeholder="Enter Last Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Designation")}
                    name="authorizedPerson.designation"
                    formik={formik}
                    disabled={
                      isDisabled && !!data?.data?.designation && !isAdmin
                    }
                    placeholder="Enter Designation"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Email") + `${isUnRegisteredPerson ? "*" : ""}`}
                    name="authorizedPerson.email"
                    disabled
                    formik={formik}
                    type="email"
                    placeholder="Enter Email"
                    // InputProps={{
                    //   endAdornment: (
                    //     <InputAdornment position="end">
                    //       {authorizedPerson.email ? (
                    //         isDifferentUser ? (
                    //           <UnverifiedMailIcon />
                    //         ) : (
                    //           <VerifiedMailIcon />
                    //         )
                    //       ) : (
                    //         <></>
                    //       )}
                    //     </InputAdornment>
                    //   ),
                    // }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={
                      t("Mobile Number") + `${isUnRegisteredPerson ? "*" : ""}`
                    }
                    name="authorizedPerson.mobileNumber"
                    disabled
                    formik={formik}
                    type="number"
                    InputProps={{
                      inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                    }}
                    placeholder="Enter Mobile Number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Land Line")}
                    type="number"
                    InputProps={{
                      inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                    }}
                    name="authorizedPerson.landLine"
                    formik={formik}
                    disabled={
                      isDisabled && !!data?.data?.designation && !isAdmin
                    }
                    placeholder="Enter Land-line Number"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AuthorizedInfoWrapper>
      </Grid>
    </>
  );
}

AuthorizedPersonDetails.propTypes = {
  formik: PropTypes.object.isRequired,
  t: PropTypes.func,
};

export default withTranslation()(AuthorizedPersonDetails);
